// src/components/shared/Calendar.jsx
import React, { useState, useMemo } from 'react';
import { monthName, getDaysInMonth, getFirstDayOfMonth } from 'shared/utils/helpers';
import { slotsInRange } from 'shared/utils/scheduleLogic';

const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export default function Calendar({
  occupiedSlots = [],
  availableSlots = null,
  onDateSelect,
  onSlotSelect,
  selectedDate,
  selectedSlot,
  workDays = null,
  workStart = '08:00',
  workEnd = '18:00',
}) {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  const todayISO = now.toISOString().split('T')[0];
  const allSlots = useMemo(() => slotsInRange(workStart, workEnd), [workStart, workEnd]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const firstDay = getFirstDayOfMonth(year, month);
  const daysInM  = getDaysInMonth(year, month);
  const cells    = [];

  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInM; d++) cells.push(d);

  return (
    <div>
      {/* Month nav */}
      <div style={{
        background: 'var(--brand)', color: '#fff', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
        padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <button onClick={prevMonth} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 34, height: 34, borderRadius: 8, cursor: 'pointer', fontSize: 18 }}>‹</button>
        <span style={{ fontFamily: "'Instrument Serif',serif", fontSize: 20 }}>{monthName(month)} {year}</span>
        <button onClick={nextMonth} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 34, height: 34, borderRadius: 8, cursor: 'pointer', fontSize: 18 }}>›</button>
      </div>

      {/* Day names */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
        {DAYS.map(d => (
          <div key={d} style={{ textAlign: 'center', padding: '10px 4px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)' }}>{d}</div>
        ))}
      </div>

      {/* Days grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 var(--radius-lg) var(--radius-lg)', overflow: 'hidden' }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`e-${idx}`} style={{ minHeight: 64, background: 'var(--surface-2)', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }} />;

          const iso      = `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const isToday  = iso === todayISO;
          const isPast   = iso < todayISO;
          const isSelected = iso === selectedDate;
          const dow      = new Date(iso + 'T12:00:00').getDay();
          // Dia de folga: fora dos dias de funcionamento configurados
          const isDayOff = workDays !== null && !workDays.includes(dow);
          const blocked  = isPast || isDayOff;

          return (
            <div key={iso} onClick={() => !blocked && onDateSelect?.(iso)}
              title={isDayOff ? 'Dia de folga do estabelecimento' : undefined}
              style={{
                minHeight: 64, padding: 8, cursor: blocked ? 'not-allowed' : 'pointer',
                borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
                background: isSelected ? 'var(--accent)' : isToday ? '#fce7eb' : blocked ? 'var(--surface-2)' : 'var(--surface)',
                opacity: blocked ? 0.45 : 1,
                transition: 'background 0.15s',
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 500,
                background: isSelected ? 'rgba(255,255,255,0.2)' : isToday && !isSelected ? 'var(--accent)' : 'transparent',
                color: isSelected ? '#fff' : isToday ? '#fff' : 'var(--text-secondary)',
              }}>
                {day}
              </div>
              {isDayOff && !isPast && (
                <div style={{ fontSize: 9, color: 'var(--text-muted)', textAlign: 'center', marginTop: 2 }}>folga</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Time slots */}
      {selectedDate && (() => {
        const now         = new Date();
        const todayISO    = now.toISOString().split('T')[0];
        const isToday     = selectedDate === todayISO;
        // Mínimo: hora atual + 30 min de antecedência
        const nowMin      = isToday ? now.getHours() * 60 + now.getMinutes() + 30 : 0;
        const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

        const baseSlots = availableSlots !== null
          ? availableSlots
          : allSlots.filter(s => !isToday || toMin(s) >= nowMin);

        const visibleSlots = availableSlots !== null
          ? baseSlots
          : baseSlots.filter(s => !occupiedSlots.includes(s));

        const allOccupied = visibleSlots.length === 0;

        return (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: 'var(--text-secondary)' }}>
              Horários disponíveis — {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { weekday:'long', day:'2-digit', month:'long' })}
            </div>
            {visibleSlots.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>
                😔 Não há mais horários disponíveis neste dia. Selecione outra data.
              </p>
            ) : (
              <div className="time-slots">
                {visibleSlots.map(slot => {
                  const isSelected = slot === selectedSlot;
                  return (
                    <div key={slot}
                      className={`time-slot ${isSelected ? 'selected' : ''}`}
                      onClick={() => onSlotSelect?.(slot)}
                    >
                      {slot}
                    </div>
                  );
                })}
              </div>
            )}
            {allOccupied && (
              <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>
                😔 Todos os horários disponíveis deste dia estão ocupados. Selecione outra data.
              </p>
            )}
          </div>
        );
      })()}
    </div>
  );
}
